import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, hasRole } from "../context/AuthContext";
import { useBootstrapWS } from "../hooks/useBootstrapWS";
import { api, extractError, normalizePaginatedList } from "../lib/api";
import DataTableExportButton from "../components/DataTableExportButton";
import DataTablePagination from "../components/DataTablePagination";
import { LIST_PAGE_SIZE_OPTIONS, DEFAULT_LIST_PAGE_SIZE } from "../lib/listPagination";
import { PageHeader, SectionCard, Pill } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import DataTableBlock from "../components/DataTableBlock";
import { useDataTable } from "../hooks/useDataTable";
import { formatIndiaDate } from "../lib/indiaTime";
import {
  buildApprovalColumns,
  flattenApprovalQueue,
  groupRowsByIntake,
} from "./stackSampleApprovalColumns";
import { PaddyLabMetricsGrid } from "../components/lab/PaddyLabMetricsDisplay";

const APPROVAL_ROLES = ["admin", "manager", "purchaser"];

export default function StackSampleApprovalPage() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [listMeta, setListMeta] = useState({ count: 0, page: 1, page_size: DEFAULT_LIST_PAGE_SIZE, total_pages: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState({});

  const canApprove = hasRole(user, APPROVAL_ROLES);

  const flatRows = useMemo(() => flattenApprovalQueue(queue), [queue]);
  const columns = useMemo(() => buildApprovalColumns(flatRows), [flatRows]);
  const table = useDataTable(flatRows, columns, { id: "lab_created_at", desc: true });
  const filteredGroups = useMemo(() => groupRowsByIntake(table.rows), [table.rows]);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/stack-sample-approval/bootstrap", { params: { page, page_size: pageSize } })
      .then((r) => {
        const body = normalizePaginatedList(r.data || {});
        setQueue(body.results || []);
        setListMeta({
          count: body.count,
          page: body.page,
          page_size: body.page_size,
          total_pages: body.total_pages,
        });
      })
      .catch((err) => {
        toast.error(extractError(err));
        setQueue([]);
        setListMeta({ count: 0, page: 1, page_size: pageSize, total_pages: 0 });
      })
      .finally(() => setLoading(false));
  }, [page, pageSize]);

  useEffect(() => {
    if (canApprove) load();
  }, [canApprove, load]);

  useBootstrapWS(["stack", "lab"], load);

  const actLine = async (lineId, decision) => {
    const path = decision === "APPROVE" ? "approve" : "reject";
    try {
      await api.post(`/stack-sample-lines/${lineId}/${path}`, {
        notes: notes[lineId] || "",
      });
      toast.success(decision === "APPROVE" ? "Stack approved" : "Stack rejected");
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const bulk = async (intakeId, contractDecision) => {
    try {
      await api.post(`/stack-samples/${intakeId}/purchaser-review`, {
        contract_decision: contractDecision,
      });
      toast.success(
        contractDecision === "APPROVE_ALL_PASSING"
          ? "All AUTO_PASS stacks approved"
          : "Pending stacks rejected",
      );
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  if (!canApprove) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-sm text-muted-foreground">
        Purchase manager or purchaser role required.
      </div>
    );
  }

  const totalPending = listMeta.count;
  const visiblePending = table.rows.length;

  return (
    <div className="space-y-8 max-w-6xl mx-auto" data-testid="stack-sample-approval-page">
      <PageHeader
        eyebrow="Quality · Purchasing"
        title="Stack sample approval"
        description="Review lab results and approve or reject each stack. Use column filters to narrow by party, item, date, or verdict."
      />


      <SectionCard
        title={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>
              {table.hasActiveFilters
                ? `${visiblePending} of ${totalPending} stack${totalPending === 1 ? "" : "s"}`
                : `Awaiting decision (${totalPending} stack${totalPending === 1 ? "" : "s"})`}
              {loading ? " · Loading…" : ""}
            </span>
          </span>
        }
        action={(
          <DataTableExportButton
            apiPath="/stack-samples/pending-approval"
            params={{}}
            columns={columns}
            filename="stack-sample-approval.xlsx"
            testId="approval-export"
          />
        )}
      >
        {totalPending > 0 && (
          <DataTableBlock
            title=""
            wrapInSectionCard={false}
            columns={columns}
            table={table}
            testId="approval-filter-table"
            showBody={false}
            className="mb-5"
          />
        )}

        {loading && <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>}
        {!loading && totalPending === 0 && (
          <div className="py-12 text-center">
            <p className="text-sm text-muted-foreground">Nothing pending approval.</p>
            <p className="text-xs text-muted-foreground mt-2">
              Samples appear here after lab submits a report for each sample ID.
            </p>
          </div>
        )}
        {!loading && totalPending > 0 && visiblePending === 0 && (
          <div className="py-12 text-center">
            <p className="text-sm text-muted-foreground">No stacks match the current filters.</p>
            <Button type="button" variant="link" size="sm" className="mt-2" onClick={table.clearAllFilters}>
              Clear filters
            </Button>
          </div>
        )}

        <DataTablePagination
          meta={listMeta}
          pageSize={pageSize}
          pageSizeOptions={LIST_PAGE_SIZE_OPTIONS}
          onPageSizeChange={setPageSize}
          onPageChange={setPage}
          testId="approval-pagination"
        />

        <div className="space-y-5 mt-4">
          {filteredGroups.map((intake) => (
            <article
              key={intake.id}
              className="rounded-lg border border-border overflow-hidden bg-card shadow-sm"
            >
              <header className="px-4 py-3 bg-muted/20 border-b flex flex-wrap items-center justify-between gap-3">
                <div>
                  <span className="font-mono text-sm font-semibold text-foreground">{intake.code}</span>
                  <span className="mx-2 text-muted-foreground">·</span>
                  <span className="text-sm">{intake.party_name}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {intake.intake_timing === "AFTER_CONTRACT" ? "After contract" : "Before contract"}
                  </span>
                  {intake.created_at && (
                    <span className="ml-2 text-xs text-muted-foreground tabular-nums">
                      · {formatDate(intake.created_at)}
                    </span>
                  )}
                  <span className="ml-2">
                    <Pill tone="gold">{intake.status?.replace(/_/g, " ")}</Pill>
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="text-xs"
                    onClick={() => bulk(intake.id, "APPROVE_ALL_PASSING")}
                  >
                    Approve all AUTO_PASS
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="text-xs text-red-700 border-red-200"
                    onClick={() => bulk(intake.id, "REJECT_ALL_PENDING")}
                  >
                    Reject all pending
                  </Button>
                </div>
              </header>
              {(intake.purchase_contracts || []).length > 0 && (
                <p className="px-4 py-2 text-xs text-muted-foreground border-b bg-muted/20">
                  Contracts:{" "}
                  {(intake.purchase_contracts || [])
                    .map((c) => `${c.contract_number} (${c.link_approval_status || "PENDING"})`)
                    .join(", ")}
                </p>
              )}
              <div className="divide-y">
                {(intake.stack_lines || []).map((line) => {
                  const lab = line.lab_report;
                  return (
                    <div key={line.id} className="p-4">
                      <div className="flex flex-wrap justify-between gap-2 mb-2">
                        <div>
                          <span className="font-mono text-xs font-bold text-primary">{line.sample_code}</span>
                          <span className="ml-2 text-sm">
                            Stack <strong>{line.stack_number}</strong> · {line.bags} bags · {line.quality}
                          </span>
                          {line.lab_created_at && (
                            <span className="ml-2 text-xs text-muted-foreground tabular-nums">
                              Lab {formatDate(line.lab_created_at)}
                            </span>
                          )}
                        </div>
                        {lab && (
                          <Pill
                            tone={
                              lab.needs_admin_review || lab.verdict === "PENDING_REVIEW"
                                ? "warning"
                                : ["AUTO_PASS", "RECORDED"].includes(lab.verdict)
                                  ? "success"
                                  : "warning"
                            }
                          >
                            {lab.needs_admin_review
                              ? "Needs review"
                              : (lab.verdict || "Recorded").replace(/_/g, " ")}
                          </Pill>
                        )}
                      </div>
                      {lab && (
                        <PaddyLabMetricsGrid lab={lab} className="mb-3" />
                      )}
                      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
                        <div className="flex-1">
                          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            Note (optional)
                          </Label>
                          <Input
                            className="mt-1 h-9"
                            value={notes[line.id] || ""}
                            onChange={(e) => setNotes((n) => ({ ...n, [line.id]: e.target.value }))}
                          />
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
                          onClick={() => actLine(line.id, "APPROVE")}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          onClick={() => actLine(line.id, "REJECT")}
                        >
                          <XCircle className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

function formatDate(iso) {
  return formatIndiaDate(iso, { dateStyle: "medium" }) || iso || "";
}

